import React, { useEffect, useState, useCallback } from 'react';
import { getInvoiceRequests, raiseInvoice } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileText, CheckCircle, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = { USD: '$', INR: '\u20B9', MYR: 'RM', AED: 'AED', SGD: 'S$' };

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [raising, setRaising] = useState(null);
  const { user } = useAuth();
  const canRaise = user?.role === 'accountant' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const { data } = await getInvoiceRequests();
      setInvoices(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRaise = async (id) => {
    setRaising(id);
    try {
      await raiseInvoice(id);
      toast.success('Invoice marked as raised. Requester notified via email.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error raising invoice');
    } finally { setRaising(null); }
  };

  const filtered = invoices.filter((inv) =>
    [inv.campaign_name, inv.client_name, inv.requested_by_name, inv.invoice_number, inv.status]
      .some((f) => (f || '').toLowerCase().includes(search.toLowerCase()))
  );

  const pendingCount = invoices.filter(i => i.status === 'pending').length;
  const raisedCount = invoices.filter(i => i.status === 'raised').length;

  if (loading) return <div className="h-64 bg-zinc-100 animate-pulse" data-testid="invoices-loading" />;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Invoice Requests</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {canRaise ? 'Review and raise invoices for campaign requests' : 'Track your invoice request statuses'}
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Requests</div>
          <div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{invoices.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Pending</div>
          <div className="font-mono text-2xl font-bold text-amber-600 mt-1">{pendingCount}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Raised</div>
          <div className="font-mono text-2xl font-bold text-emerald-600 mt-1">{raisedCount}</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Search invoices..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-none border-zinc-300" data-testid="invoice-search-input" />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <FileText size={40} className="mx-auto text-zinc-300 mb-4" />
          <p className="text-zinc-500 text-sm">No invoice requests found</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="invoices-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Campaign</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Client</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Buy Type</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Volume</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Price</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Budget</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Period</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Requested By</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
                {canRaise && <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Action</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const sym = CURRENCY_SYMBOLS[inv.currency] || inv.currency || '$';
                return (
                  <tr key={inv.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-medium text-zinc-900">{inv.campaign_name}</td>
                    <td className="px-4 py-3 text-zinc-600">{inv.client_name}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold px-2 py-0.5 bg-zinc-100 text-zinc-700">{inv.buy_type}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{(inv.volume || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono">{sym}{(inv.price || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{sym}{(inv.budget || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{inv.start_date || '-'} to {inv.end_date || '-'}</td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">
                      <div>{inv.requested_by_name}</div>
                      <div className="text-zinc-400">{inv.created_at ? inv.created_at.split('T')[0] : ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      {inv.status === 'raised' ? (
                        <div>
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} /> Raised
                          </span>
                          {inv.raised_by_name && (
                            <div className="text-xs text-zinc-400 mt-1">by {inv.raised_by_name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-amber-100 text-amber-700">
                          <Clock size={12} /> Pending
                        </span>
                      )}
                    </td>
                    {canRaise && (
                      <td className="px-4 py-3 text-right">
                        {inv.status === 'pending' ? (
                          <Button
                            size="sm"
                            onClick={() => handleRaise(inv.id)}
                            disabled={raising === inv.id}
                            className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700 gap-1 text-xs h-8"
                            data-testid={`raise-invoice-${inv.id}`}
                          >
                            <CheckCircle size={12} /> {raising === inv.id ? 'Raising...' : 'Raise Invoice'}
                          </Button>
                        ) : (
                          <span className="text-xs text-zinc-400">Done</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
